import { getLocalTimeZone, now } from "@internationalized/date"
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { FormEvent, useState } from "react"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Separator from "@/globalComponents/atoms/Separator"
import StripeElementsWrapper from "@/globalComponents/molecules/StripeElementsWrapper"
import {
  ExpectedError,
  GatedPaymentIntentError,
} from "@/utilities/expectedErrors"
import {
  checkoutQueryOptions,
  confirmPaymentIntentQueryOptions,
  paymentIntentQueryOptions,
} from "@/utilities/hooks/queries/payment"
import { useStripePaymentElement } from "@/utilities/hooks/useStripePaymentElement"
import { log } from "@/utilities/logging"
import { prettyCurrencyNumber } from "@/utilities/normalizers"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/$pamID/$sessionID/checkout"
)({
  staticData: {
    pageName: "Checkout",
  },
  beforeLoad: async ({ context, params }) => {
    const rsp = await context.queryClient.fetchQuery(
      paymentIntentQueryOptions({
        sessionID: params.sessionID,
        pamID: params.pamID,
      })
    )
    if (!rsp.requiresPayment) {
      const payRsp = await context.queryClient.fetchQuery(
        confirmPaymentIntentQueryOptions({
          stripePaymentIntentID: null,
          pamID: params.pamID,
        })
      )

      throw redirect({
        to: "/receipt/$receiptID",
        params: { receiptID: payRsp.receiptID },
      })
    }

    return { paymentIntent: rsp }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { qrLookupID, pamID, sessionID } = Route.useParams()
  const { paymentIntent } = Route.useRouteContext()
  const { t } = useTranslation(["gated"])

  const { data: checkout } = useSuspenseQuery(
    checkoutQueryOptions({ sessionID })
  )

  const baseUrl = window.location.origin
  const returnURL = `${baseUrl}/qr/${qrLookupID}/gated/${pamID}/${sessionID}/payment-status`

  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("inlane.checkout.header")}
      </Heading>

      <StripeElementsWrapper
        clientSecret={paymentIntent.clientSecret!}
        customerSessionClientSecret={
          paymentIntent.customerSessionClientSecret || undefined
        }
        flow="gated"
      >
        <Card>
          <Heading level={1} as={2}>
            {t("inlane.checkout.subhead")}
          </Heading>
          <CheckoutForm
            sessionID={sessionID}
            pamID={pamID}
            returnURL={returnURL}
            amountDue={checkout.transactionDetails.amountDue}
            goNext={(receiptID: string) =>
              navigate({
                to: "/receipt/$receiptID",
                params: { receiptID },
              })
            }
          />
        </Card>
      </StripeElementsWrapper>
    </>
  )
}

interface CheckoutFormProps {
  goNext: (receiptID: string) => void
  returnURL: string
  amountDue: number
  pamID: string
  sessionID: string
}

function CheckoutForm(props: CheckoutFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const { t } = useTranslation(["gated", "common", "errors"])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error>()

  const { paymentElementProps, stripeError } = useStripePaymentElement({
    flow: "gated",
  })

  if (stripeError) throw stripeError

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={!stripe || !elements || loading}>
        <PaymentElement allow-top-navigation {...paymentElementProps} />
        <Separator
          orientation="horizontal"
          style={{ marginTop: "var(--space-5)" }}
        />
        <dl>
          <div style={{ marginTop: "var(--space-5)" }}>
            <dt>
              <Heading level={4} as={3}>
                {t("inlane.checkout.due", { ns: "gated" })}
              </Heading>
            </dt>
            <dd>
              <p className="headline--4">
                {prettyCurrencyNumber(props.amountDue)}
              </p>
            </dd>
          </div>
        </dl>
        {error && (
          <FieldError>
            {error instanceof ExpectedError
              ? t(error.translationKey, { ns: "errors" })
              : error.message}
          </FieldError>
        )}
        <Button type="submit" buttonType="primary" isPending={loading}>
          {t("common.submit", { ns: "common" })}
          <Icon name="arrowRight" slot="right" />
        </Button>
      </fieldset>
    </form>
  )

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setLoading(true)
    if (!stripe || !elements) {
      setLoading(false)
      return
    }

    const paymentElement = elements.getElement(PaymentElement)
    if (!paymentElement) {
      setError(new Error(t("common.paymentDetailsNotFound", { ns: "errors" })))
      setLoading(false)
      return
    }

    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: props.returnURL,
        },
      })

      if (result.error) {
        log({
          level: "error",
          flow: "gated",
          scope: "payment.stripeConfirmPayment",
          message: "Stripe confirmPayment returned an error",
          meta: {
            code: result.error.code,
            type: result.error.type,
            declineCode: result.error.decline_code,
          },
          sentry: "exception",
          exception: result.error,
        })
        setError(new Error(result.error.message))
        return
      }

      const rsp = await api.gated.pay.POST({
        stripePaymentIntentID: result.paymentIntent.id,
        termsAndConditionsOK: now(getLocalTimeZone()).toAbsoluteString(),
        pamID: props.pamID,
      })
      if ("failReason" in rsp) {
        throw new GatedPaymentIntentError(rsp.failReason)
      }
      props.goNext(rsp.receiptID)
    } catch (e: unknown) {
      log({
        level: "error",
        flow: "gated",
        scope: "payment.confirmPayment",
        message: "Gated payment confirmation failed",
        meta: { error: e },
        sentry: e instanceof ExpectedError ? "breadcrumb" : "exception",
        exception: e instanceof Error ? e : new Error(String(e)),
      })
      if (e instanceof Error) {
        setError(e)
      } else {
        setError(new Error(t("common.somethingWentWrong", { ns: "errors" })))
      }
    } finally {
      setLoading(false)
    }
  }
}
